import type { ReactNode } from 'react';
import { useId } from 'react';
import { useSelect } from 'downshift';

import { onlyText } from '@shieldpay/utilities-ui/only-text';

import { usePositionedPopup } from '../../hooks/use-positioned-popup/use-positioned-popup';
import { Text } from '../text/text';

import {
  type ControlledProps,
  type DropdownItems,
  type DropdownSize,
  Dropdown,
} from './dropdown-shared';
import { getSharedProps } from './utilities';

export interface DropdownProps<Value = string> extends ControlledProps<Value> {
  /* NOTE: the label on each item cannot use <FormattedMessage />
   * because the onlyText function cannot extract the text.
   **/
  items: DropdownItems<Value>;
  label: ReactNode;
  placeholder?: string;
  size?: DropdownSize;
  disabled?: boolean;
  expand?: boolean;
}

export const DropdownSingle = <Value,>({
  disabled = false,
  items,
  label,
  placeholder,
  size = 'medium',
  onChange,
  value,
  expand = false,
}: DropdownProps<Value>) => {
  // id needs to be consistent between server and client
  const id = useId();

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    id: `dropdown-${id}`,
    items,
    // This runs on each item whenever a keypress happens on the button
    // to allow us to match a string to the label, which will be a ReactNode
    // Do not use <FormattedMessage />, use formatMessage. See OPTIMUS-600 */
    itemToString: (item) => onlyText(item?.label),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem?.value) {
        onChange(selectedItem.value);
      }
    },
    selectedItem:
      value === ''
        ? ({ value: '', label: null } as const)
        : items.find((item) => item.value === value),
  });

  const { setFloating, setReference, floatingStyles, constrainStyles } =
    usePositionedPopup({
      open: isOpen,
    });

  const { itemPadding } = getSharedProps({
    size,
    disabled,
  });

  const { 'aria-activedescendant': ariaActivedescendant, ...toggleProps } =
    getToggleButtonProps({
      ref: setReference,
    });

  // Only include aria-activedescendant if it is a non-empty string
  // This is a workaround for a WCAG issue. Downshift can pass down an empty string
  // which is invalid
  const formattedToggleProps = {
    ...toggleProps,
    ...(ariaActivedescendant && ariaActivedescendant.length > 0
      ? { 'aria-activedescendant': ariaActivedescendant }
      : { 'aria-activedescendant': undefined }),
  };

  return (
    <Dropdown
      expand={expand}
      label={label}
      disabled={disabled}
      labelProps={getLabelProps()}
    >
      <Dropdown.Button
        size={size}
        disabled={disabled}
        isOpen={isOpen}
        toggleButtonProps={formattedToggleProps}
        toggleButtonVariant="container"
      >
        <Text
          truncate
          color="currentColor"
          variant="input"
          padding={itemPadding.text}
        >
          {selectedItem?.label || placeholder}
        </Text>
      </Dropdown.Button>

      <Dropdown.Menu
        menuProps={getMenuProps()}
        floatingStyles={floatingStyles}
        setFloating={setFloating}
        disabled={disabled}
        isOpen={isOpen}
        constrainStyles={constrainStyles}
      >
        {items.map((item, index) => (
          <Dropdown.MenuItem
            highlightedIndex={highlightedIndex}
            item={item}
            index={index}
            size={size}
            key={`${item.value || index}`}
            isSelected={selectedItem?.value === item.value}
            {...getItemProps({ item, index })}
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};
